import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BRAND_COLORS, type LedgerKind, type MeResponse } from '@lk/shared';
import { Button } from '../../components/Button';
import { Panel, Stat } from '../../components/Panel';
import { EmptyState, ErrorState, LoadingState } from '../../components/States';
import { formatDateTime, hryvniaLabel } from '../../lib/format';
import { humanMessage } from '../../lib/errors';
import { payout, type WalletResponse } from '../../lib/api';
import { walletQuery } from '../learner/queries';
import { coinReasonLabel } from '../quiz/coins';
import { useSelectedLearner } from './ParentLayout';

const KIND_LABEL: Record<LedgerKind, string> = {
  earn: 'за відповідь',
  session_bonus: 'бонус за сесію',
  goal_bonus: 'бонус за ціль',
  achievement: 'досягнення',
  payout: 'виплата готівкою',
  adjustment: 'ручна правка',
};

export function WalletPage({ me }: { me: MeResponse }): React.JSX.Element {
  const learner = useSelectedLearner(me);
  const queryClient = useQueryClient();
  const wallet = useQuery({ ...walletQuery(learner?.id ?? ''), enabled: Boolean(learner?.id) });

  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  const pay = useMutation<WalletResponse, Error, { amount: number; note: string }>({
    mutationFn: (input) =>
      payout({
        learnerId: learner?.id ?? '',
        amount: input.amount,
        ...(input.note ? { note: input.note } : {}),
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(walletQuery(learner?.id ?? '').queryKey, next);
      setAmount('');
      setNote('');
    },
  });

  if (!learner) return <EmptyState title="Немає дитини для показу" />;
  if (wallet.isLoading) return <LoadingState label="Відкриваємо гаманець…" />;
  if (wallet.isError) return <ErrorState error={wallet.error} onRetry={() => void wallet.refetch()} />;

  const data = wallet.data;
  if (!data) return <EmptyState title="Гаманець порожній" />;

  const parsedAmount = Number(amount);
  const canPay =
    Number.isFinite(parsedAmount) && parsedAmount >= 1 && parsedAmount <= Math.max(0, data.balance);

  return (
    <div className="flex flex-col gap-4">
      <Panel className="p-5">
        <h2 className="text-lg font-black">Гаманець · {learner.displayName}</h2>
        <p className="mt-1 text-xs font-semibold text-white/55">
          Баланс — це сума журналу, а не окреме число. Тому «бали пропали» тут неможливе за
          побудовою: кожне нарахування і кожна виплата лишаються окремим записом.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat label="До виплати" value={hryvniaLabel(data.balance)} tone={BRAND_COLORS.gold} />
          <Stat label="Зароблено всього" value={hryvniaLabel(data.totalEarned)} />
          <Stat label="Виплачено" value={hryvniaLabel(data.totalPaidOut)} />
          <Stat
            label="Сьогодні"
            value={`${data.earnedToday}/${data.dailyCap}`}
            hint={`за тиждень ${data.earnedThisWeek}`}
            tone={BRAND_COLORS.cyan}
          />
        </div>
      </Panel>

      <Panel className="p-5">
        <h3 className="text-base font-black">Виплатити готівкою</h3>
        <form
          className="mt-3 flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canPay) return;
            pay.mutate({ amount: Math.round(parsedAmount), note });
          }}
        >
          <label className="text-xs font-black tracking-widest text-white/50 uppercase">
            Сума, грн
            <input
              type="number"
              min={1}
              max={Math.max(1, data.balance)}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="lk-panel-tight tabular mt-1 h-12 w-32 px-3 text-lg font-black outline-none"
            />
          </label>
          <label className="min-w-[12rem] flex-1 text-xs font-black tracking-widest text-white/50 uppercase">
            Нотатка
            <input
              type="text"
              value={note}
              maxLength={300}
              placeholder="напр. кишенькові за тиждень"
              onChange={(event) => setNote(event.target.value)}
              className="lk-panel-tight mt-1 h-12 w-full px-3 text-sm font-bold outline-none"
            />
          </label>
          <Button
            type="submit"
            tone="gold"
            mute
            loading={pay.isPending}
            disabled={!canPay || pay.isPending}
          >
            Виплатити
          </Button>
          <button
            type="button"
            className="lk-chip cursor-pointer"
            onClick={() => setAmount(String(data.balance))}
          >
            усе ({data.balance})
          </button>
        </form>
        {pay.isError ? (
          <p className="mt-2 text-sm font-bold text-[var(--lk-red)]" role="alert">
            {humanMessage(pay.error)}
          </p>
        ) : null}
        {pay.isSuccess ? (
          <p className="mt-2 text-sm font-bold text-[var(--lk-green)]" role="status">
            Виплату записано в журнал.
          </p>
        ) : null}
      </Panel>

      <Panel className="p-5">
        <h3 className="text-base font-black">Журнал нарахувань</h3>
        {data.recentEntries.length === 0 ? (
          <EmptyState title="Записів ще немає" hint="Тут зʼявляться всі нарахування і виплати." />
        ) : (
          <div className="lk-scroll-x mt-3">
            <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
              <thead>
                <tr className="text-[11px] font-black tracking-widest text-white/45 uppercase">
                  <th className="py-2 pr-3">Коли</th>
                  <th className="py-2 pr-3">Тип</th>
                  <th className="py-2 pr-3">За що</th>
                  <th className="py-2 pr-3 text-right">Сума</th>
                </tr>
              </thead>
              <tbody>
                {data.recentEntries.map((entry) => (
                  <tr key={entry.id} className="border-t border-white/10">
                    <td className="tabular py-2 pr-3 font-bold text-white/70">
                      {formatDateTime(entry.createdAt)}
                    </td>
                    <td className="py-2 pr-3 font-bold">{KIND_LABEL[entry.kind]}</td>
                    <td className="py-2 pr-3 text-xs font-semibold text-white/60">
                      {entry.reasons.map(coinReasonLabel).join(', ') || entry.note || '—'}
                    </td>
                    <td
                      className="tabular py-2 pr-3 text-right font-black"
                      style={{
                        color: entry.amount >= 0 ? BRAND_COLORS.green : BRAND_COLORS.gold,
                      }}
                    >
                      {entry.amount >= 0 ? `+${entry.amount}` : entry.amount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
